import { useAuth0 } from "@auth0/auth0-react";
import { DEFAULT_LOCALE, DEFAULT_PARTNER } from "@pandas/core-ui";
import { IconHome, IconMasksTheater, IconX } from "@tabler/icons-react";
import { useLocation, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStore } from "../../store";
import { clearStorageData } from "../../utils/session/storage";
import { BurgerMenuSvg } from "../core/Icons/BurgerMenu";
import { CheckVoucherSvg } from "../core/Icons/CheckVoucher";
import { TransactionsSvg } from "../core/Icons/Transactions";
import Loader from "../core/Loaders/Loader";
import LocationPickerModal from "../Home/LocationPicker";
import LanguageSwitcher from "./LanguageSwitcher";
import NavMenuItem from "./NavMenuItem";

const UserMenu = () => {
	const locationId = useStore((state) => state.location?.id);
	const [showUserMenu, setShowUserMenu] = useState(false);
	const theming = useStore((state) => state.config?.theming);
	const menuRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { locale = DEFAULT_LOCALE, partner = DEFAULT_PARTNER } = useParams({ strict: false }) as {
		locale?: string;
		partner?: string;
	};
	const { t } = useTranslation();
	const clearHasPickedLocation = useStore((state) => state.clearHasPickedLocation);
	const clearPartnerData = useStore((state) => state.clearPartnerData);
	const [loading, setLoading] = useState(false);
	const hasPickedLocation = useStore((state) => state.hasPickedLocation);
	const [showLocationPicker, setShowLocationPicker] = useState(false);

	const { user, logout } = useAuth0();
	const isAdmin = user?.platform_roles?.includes("RetailApp Admin") ?? false;

	const handleClickOutside = (event: MouseEvent) => {
		if (menuRef.current && event.target instanceof Node && !menuRef.current.contains(event.target)) {
			setShowUserMenu(false);
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: handleClickOutside is not memoized, adding it would cause unnecessary re-runs
	useEffect(() => {
		if (showUserMenu) {
			document.addEventListener("mousedown", handleClickOutside);
		} else {
			document.removeEventListener("mousedown", handleClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [showUserMenu]);

	const handleLogout = () => {
		setLoading(true);
		clearStorageData();
		clearPartnerData();
		clearHasPickedLocation();
		logout({ logoutParams: { returnTo: window.location.origin } });
	};

	if (loading)
		return (
			<div className="z-50 h-screen w-full backdrop-blur-sm absolute top-0 left-0">
				<div className="flex flex-row min-h-screen justify-center items-center">
					<Loader />
				</div>
			</div>
		);

	return (
		<>
			{isAdmin && !hasPickedLocation && (
				<LocationPickerModal
					showPopup={showLocationPicker}
					setShowPopup={setShowLocationPicker}
				/>
			)}
			<div>
				<div
					className="relative cursor-pointer"
					ref={menuRef}
				>
					{showUserMenu ? (
						<IconX
							size={32}
							stroke={1.5}
							onClick={() => setShowUserMenu(!showUserMenu)}
							color={theming?.navbarButtonColor}
						/>
					) : (
						<div onClick={() => setShowUserMenu(!showUserMenu)}>
							<BurgerMenuSvg strokeColor={theming?.navbarButtonColor} />
						</div>
					)}
					{showUserMenu && (
						<div className="select-none drop-shadow-lg rounded-xl absolute z-20 p-4 gap-2 bg-white right-0 min-w-[15rem] pl-sm py-sm shadow-[0px_8px_16px_0px_rgba(0,0,0,0.10)]">
							<NavMenuItem
								icon={
									<IconHome
										color={"black"}
										height={24}
										width={32}
										stroke={2}
									/>
								}
								label={t("NavigationBar.Homepage")}
								isActive={pathname.endsWith("/home")}
								onClick={() => {
									setShowUserMenu(false);
									navigate({ to: "/$locale/$partner/home", params: { locale, partner } });
								}}
							/>
							<NavMenuItem
								icon={<CheckVoucherSvg />}
								label={t("NavigationBar.Check Voucher")}
								isActive={pathname.includes("/voucher")}
								onClick={() => {
									setShowUserMenu(false);
									navigate({ to: "/$locale/$partner/voucher", params: { locale, partner } });
								}}
							/>
							<NavMenuItem
								icon={<TransactionsSvg />}
								label={t("NavigationBar.Transactions")}
								isActive={pathname.endsWith("/transactions")}
								onClick={() => {
									setShowUserMenu(false);
									navigate({
										to: "/$locale/$partner/transactions",
										params: { locale, partner },
										search: {
											page: 1,
											startDate: undefined,
											endDate: undefined,
											locationId: isAdmin ? locationId : undefined,
										},
									});
								}}
							/>
							{isAdmin && (
								<NavMenuItem
									icon={
										<IconMasksTheater
											color={"black"}
											height={24}
											width={32}
											stroke={2}
										/>
									}
									label={t("NavigationBar.Change Location")}
									isActive={false}
									onClick={() => {
										clearHasPickedLocation();
										setShowUserMenu(false);
										setShowLocationPicker(true);
									}}
								/>
							)}

							<div className="border-b-2 border-grey pt-4 mx-4 w-44"></div>
							<LanguageSwitcher />
							<div className="flex items-center w-full">
								<p
									className="text-base font-semibold text-bold text-red px-4 py-1 cursor-pointer"
									onClick={handleLogout}
								>
									{t("NavigationBar.Logout")}
								</p>
							</div>
						</div>
					)}
				</div>
			</div>
		</>
	);
};

export default UserMenu;
